import { HelpCircle } from 'lucide-react';
import React from 'react';

import { IntakeLink, MatterLink } from '@/components/entity-links/entities';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { SearchResult } from '@/services/api/searchService';

interface DuplicateSuggestionsProps {
  suggestions: SearchResult[];
  isLoading?: boolean;
}

export const DuplicateSuggestions: React.FC<DuplicateSuggestionsProps> = ({
  suggestions,
  isLoading = false,
}) => {
  if (isLoading || suggestions.length === 0) {
    return null;
  }

  // Separate matters and intakes
  const matters = suggestions.filter((s) => s.entity_type === 'matter');
  const intakes = suggestions.filter((s) => s.entity_type === 'intake');

  return (
    <div className="text-sm text-gray-600 space-y-2 pl-1 mt-2">
      {/* Show matter duplicates */}
      {matters.length > 0 && (
        <div className="flex items-center justify-between gap-2">
          <div>
            <span>Similar matter{matters.length > 1 ? 's' : ''} found: </span>
            {matters.map((matter, index) => (
              <span key={matter.entity_id}>
                {index > 0 && ', '}
                <MatterLink entityId={matter.entity_id} openInNewTab>
                  {matter.label}
                </MatterLink>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Show intake duplicates */}
      {intakes.length > 0 && (
        <div className="flex items-center justify-between gap-2">
          <div>
            <span>Similar intake{intakes.length > 1 ? 's' : ''} found: </span>
            {intakes.map((intake, index) => (
              <span key={intake.entity_id}>
                {index > 0 && ', '}
                <IntakeLink entityId={intake.entity_id} openInNewTab>
                  {intake.label}
                </IntakeLink>
              </span>
            ))}
          </div>
          <TooltipProvider delayDuration={200}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  className="inline-flex items-center text-gray-400 hover:text-gray-600 flex-shrink-0"
                >
                  <HelpCircle className="h-4 w-4" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="right" className="max-w-xs">
                <p className="text-xs">
                  Consider converting this intake to a matter instead of
                  creating a new one
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}
    </div>
  );
};
